/**
 * useFeedReaderShortcuts
 * ---
 * Extracted from `src/pages/MyFeedReader.tsx` for size discipline. Wires the
 * page-level keyboard navigation: j/k/arrows, o/Enter, s, e, r, g-prefix
 * filter switches, and `/` to focus search.
 */
import { useEffect, type RefObject } from "react";
import type { FeedFilter, FeedItem } from "@/lib/feedReader";

type Args = {
  visibleItems: FeedItem[];
  selectedItemId: string | null;
  selectItem: (id: string) => void;
  setFilter: (f: FeedFilter) => void;
  setSearchInput: (v: string) => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
  onOpenExternal: (item: FeedItem) => void;
  onStar: (item: FeedItem) => Promise<void> | void;
  onArchive: (item: FeedItem) => Promise<void> | void;
  onToggleRead: (item: FeedItem) => Promise<void> | void;
};

export const useFeedReaderShortcuts = ({
  visibleItems,
  selectedItemId,
  selectItem,
  setFilter,
  setSearchInput,
  searchInputRef,
  onOpenExternal,
  onStar,
  onArchive,
  onToggleRead,
}: Args) => {
  useEffect(() => {
    let lastG = 0;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
          if (e.key === "Escape" && tag === "INPUT" && target === searchInputRef.current) {
            setSearchInput("");
            target.blur();
          }
          return;
        }
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (Date.now() - lastG < 800) {
        lastG = 0;
        if (e.key === "u") { setFilter("unread"); e.preventDefault(); return; }
        if (e.key === "s") { setFilter("starred"); e.preventDefault(); return; }
        if (e.key === "a") { setFilter("archive"); e.preventDefault(); return; }
        if (e.key === "g") { setFilter("all"); e.preventDefault(); return; }
      }
      if (e.key === "g") { lastG = Date.now(); return; }

      if (e.key === "/") {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      if (!visibleItems.length) return;

      const idx = selectedItemId
        ? visibleItems.findIndex((it) => it.id === selectedItemId)
        : -1;
      const focused = idx >= 0 ? visibleItems[idx] : null;

      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        const nextIdx = Math.min(visibleItems.length - 1, idx + 1);
        selectItem(visibleItems[Math.max(0, nextIdx)].id);
        return;
      }
      if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        const prevIdx = idx <= 0 ? 0 : idx - 1;
        selectItem(visibleItems[prevIdx].id);
        return;
      }
      if (!focused) return;
      if (e.key === "o" || e.key === "Enter") { e.preventDefault(); onOpenExternal(focused); return; }
      if (e.key === "s") { e.preventDefault(); void onStar(focused); return; }
      if (e.key === "e") { e.preventDefault(); void onArchive(focused); return; }
      if (e.key === "r") { e.preventDefault(); void onToggleRead(focused); return; }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleItems, selectedItemId, selectItem]);
};
